import { WidgetProps } from '@rjsf/utils';
import React, { useCallback, useMemo, useState } from 'react';
import { useAsync } from 'react-use';

import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { TextField } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Autocomplete } from '@material-ui/lab';

const deduplicateOptions = (array: { name: string; title?: string }[]) => {
  const map = new Map<string, { name: string; title?: string }>();
  array.forEach(opt => {
    if (opt && opt.name) {
      map.set(opt.name, opt);
    }
  });
  return Array.from(map.values());
};

const renderOption = (option: { name: string; title?: string }) => (
  <React.Fragment>{option.title || option.name}</React.Fragment>
);

export const EntityKindTypeahead = (props: WidgetProps) => {
  const { allowedKinds, filterBySpecType } = props.options as {
    allowedKinds?: string[];
    filterBySpecType?: string;
  };
  const catalogApi = useApi(catalogApiRef);
  const [search, setSearch] = useState<string>('');
  const [entityRefs, setEntityRefs] = useState<
    { name: string; title?: string }[]
  >([]);

  const { loading: loadingAssignees } = useAsync(async () => {
    const queryFilter: Record<string, any> = {};

    if (
      allowedKinds &&
      Array.isArray(allowedKinds) &&
      allowedKinds.length > 0
    ) {
      queryFilter.kind = allowedKinds;
    }

    if (filterBySpecType && typeof filterBySpecType === 'string') {
      queryFilter['spec.type'] = filterBySpecType;
    }

    const queryEntitiesResponse = await catalogApi.queryEntities({
      fullTextFilter: {
        term: search,
        fields: ['metadata.name', 'metadata.title'],
      },
      filter: Object.keys(queryFilter).length > 0 ? queryFilter : undefined,
    });
    setEntityRefs(
      deduplicateOptions([
        ...entityRefs,
        ...queryEntitiesResponse.items.map(value => ({
          name: value.metadata.name ?? '',
          title: value.metadata.title,
        })),
      ]),
    );
  }, [search]);

  // Ensure controlled values are displayable even before user searches.
  useAsync(async () => {
    let rawValues: string[] = [];
    if (props.value) {
      rawValues = Array.isArray(props.value) ? props.value : [props.value];
    }

    const values = rawValues
      .filter(v => typeof v === 'string' && v.length > 0)
      .map(v => String(v));

    if (values.length === 0) return;

    const missing = values.filter(
      v => !entityRefs.some(opt => opt.name.toLowerCase() === v.toLowerCase()),
    );
    if (missing.length === 0) return;

    const entities = await catalogApi.queryEntities({
      filter: missing.map(name => ({
        ...(allowedKinds &&
        Array.isArray(allowedKinds) &&
        allowedKinds.length > 0
          ? { kind: allowedKinds }
          : {}),
        ...(filterBySpecType ? { 'spec.type': filterBySpecType } : {}),
        'metadata.name': name,
      })),
    });

    setEntityRefs(prev =>
      deduplicateOptions([
        ...prev,
        ...entities.items.map(value => ({
          name: value.metadata.name ?? '',
          title: value.metadata.title,
        })),
      ]),
    );
  }, [props.value, allowedKinds, filterBySpecType, entityRefs, catalogApi]);

  const value = useMemo(() => {
    if (!props.value) return props.multiple ? [] : null;
    const values = Array.isArray(props.value) ? props.value : [props.value];
    return props.multiple
      ? entityRefs.filter(opt => values.includes(opt.name))
      : entityRefs.find(opt => opt.name === values[0]) || null;
  }, [props.value, entityRefs, props.multiple]);

  // Handler for Autocomplete value change
  const handleChange = useCallback(
    (_: any, selected: any) => {
      let newNameValues;
      if (Array.isArray(selected)) {
        newNameValues = selected.map((opt: any) => opt.name);
      } else if (selected) {
        newNameValues = selected.name;
      } else {
        newNameValues = props.multiple ? [] : undefined;
      }
      props.onChange(newNameValues);
    },
    [props],
  );

  // Handler for Autocomplete input change
  const handleInputChange = useCallback(
    (_: any, newInputValue: string, reason: string) => {
      if (reason === 'input') {
        if (newInputValue && newInputValue.length > 2) {
          setSearch(newInputValue);
        } else if (!newInputValue) {
          setSearch('');
        }
      } else if (reason === 'clear') {
        setSearch('');
      }
    },
    [],
  );

  const handleRenderOption = useCallback(
    (option: { name: string; title?: string }) => renderOption(option),
    [],
  );

  return (
    <Autocomplete
      multiple={!!props.multiple}
      disableCloseOnSelect={!!props.multiple}
      loading={loadingAssignees}
      options={entityRefs}
      value={value}
      disabled={props.disabled}
      getOptionLabel={option => option.title || option.name}
      getOptionSelected={(option, selectedValue) =>
        option.name === selectedValue.name
      }
      onChange={handleChange}
      onInputChange={handleInputChange}
      renderOption={handleRenderOption}
      popupIcon={<ExpandMoreIcon />}
      renderInput={params => (
        <TextField {...params} label={props.label} fullWidth />
      )}
    />
  );
};
